import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useAuthStore } from "../../auth/store";
import { useNotify } from "../../components/notifications";
import { useSaveFlow } from "../../components/confirm";
import { AppDialog } from "../../components/AppDialog/AppDialog";
import { AiCombo } from "../../components/AiCombo";
import { MdiTemplatesDetail } from "./MdiTemplatesDetail";

/**
 * Settings → Master Data Initialization → Initialization Log — the "New" popup
 * (legacy InitializationLogDetail_DetailView). Opened from the list's New button.
 * Date · MDI Name (a saved template) · Template (Browse / Import / Reset) + a read-only
 * grid of the chosen MDI's templates. Save writes an Initialization Log entry
 * (header + one row per template). The actual Import EXECUTION is deferred — the
 * Import button explains it is a later slice (owner decision 2026-10-01).
 */

interface Props {
  id: string | null;
  title?: string;
  group?: string;
  onHome?: () => void;
  onSaved: () => void;
  onClose: () => void;
}

interface MdiOpt { id: string; name: string; }
interface TplRow { templateName: string; importIndex: number; query: string; }

const API = "/api/v1/mdi-templates";
const today = () => new Date().toISOString().slice(0, 10);

export function MdiLogDetail({ onSaved, onClose }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  // ImportedBy shows the current user (the importer), matching legacy (owner report 2026-10-22).
  const currentUserName = useAuthStore((s) => s.user?.name ?? "");
  const [date, setDate] = useState(today());
  const [mdiOid, setMdiOid] = useState("");
  const [options, setOptions] = useState<MdiOpt[]>([]);
  const [rows, setRows] = useState<TplRow[]>([]);
  const [pendingFile, setPendingFile] = useState<string>("");
  // QueryEditor popup — read-only view of a template row's import query (legacy link).
  const [queryView, setQueryView] = useState<string | null>(null);
  // "+ Add MDI Name" popup — opens the FULL Initialization Templates editor
  // (MDI Version · MDI Name* · Attachment Browse/Load/Reset · Templates grid,
  // legacy image 397) reused via <MdiTemplatesDetail embedded>; on save it selects
  // the new MDI here (owner request 2026-10-02).
  const [addOpen, setAddOpen] = useState(false);

  const loadOptions = useCallback(async () => {
    try {
      const res = await api.get(`${API}/mdi-options`);
      setOptions((res.data as { data: MdiOpt[] }).data ?? []);
    } catch { /* leave the picker empty */ }
  }, []);
  useEffect(() => { void loadOptions(); }, [loadOptions]);

  // Picking an MDI loads its templates into the grid (read-only preview of the import plan).
  useEffect(() => {
    if (!mdiOid) { setRows([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${API}/${mdiOid}`);
        const d = (res.data as { data: { templates?: Record<string, unknown>[] } }).data;
        if (cancelled) return;
        setRows((d.templates ?? []).map((r) => ({
          templateName: String(r.templateName ?? ""),
          importIndex: Number(r.importIndex ?? 0),
          query: String(r.query ?? ""),
        })));
      } catch { if (!cancelled) setRows([]); }
    })();
    return () => { cancelled = true; };
  }, [mdiOid]);

  const onImport = () =>
    notify.warning("Import execution is not available yet (deferred). You can still save this as a log entry.");

  const doSave = async () => {
    if (!mdiOid) { notify.error("MDI Name must be selected."); return; }
    await runSave({
      formName: "Initialization Log",
      auditAction: "create",
      save: async () => { await api.post(`${API}/log`, { date, mdiOid }); },
      onSaved: () => onSaved(),
    });
  };

  return (
    <AppDialog
      open
      title="Initialization Log Details"
      icon="ti-database-import"
      width={900}
      onClose={onClose}
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={() => void doSave()}>
          Save
        </button>
        <button className="ca-btn" onClick={onClose}>
          Cancel
        </button>
      </>}
    >
      <div className="mlog-form">
        <label className="mlog-row">
          <span className="mlog-lbl">Date:</span>
          <input type="date" className="mlog-in" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <div className="mlog-row">
          <span className="mlog-lbl">MDI Name:</span>
          {/* Shared XAF lookup editor (AiCombo) — value + welded "＋ New" + caret as one
              control, matching the Sample Registration combos (owner request 2026-10-16,
              replacing the native <select> + separate "+" button). */}
          <div className="mlog-mdi">
            <AiCombo
              value={mdiOid || null}
              options={options.map((o) => ({ value: o.id, label: o.name }))}
              onChange={(v) => setMdiOid(v ?? "")}
              onNew={() => setAddOpen(true)}
              newTitle="Add MDI Name"
              placeholder="— Select —"
            />
          </div>
        </div>
        <div className="mlog-row">
          <span className="mlog-lbl">Template:</span>
          <div className="mlog-att">
            <input type="file" accept=".xlsx" className="mlog-file" onChange={(e) => setPendingFile(e.target.files?.[0]?.name ?? "")} />
            <button className="ca-btn" onClick={onImport} disabled={!pendingFile}><i className="ti ti-file-import" /> Import</button>
            <button className="ca-btn" onClick={() => setPendingFile("")}><i className="ti ti-refresh" /> Reset</button>
          </div>
        </div>
      </div>

      <h4 className="mlog-sec">Templates</h4>
      <div className="mlog-gridwrap">
        <table className="mlog-grid">
          <thead>
            <tr>
              <th style={{ width: 200 }}>TemplateName</th>
              <th style={{ width: 90 }}>ImportIndex</th>
              <th style={{ width: 240 }}>QueryEditor</th>
              <th style={{ width: 90 }}>Duration</th>
              <th style={{ width: 150 }}>ImportedBy</th>
              <th style={{ width: 120 }}>ImportStatus</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="mlog-empty">No data to display</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i}>
                <td>{r.templateName}</td>
                <td>{r.importIndex}</td>
                <td>
                  {/* Legacy per-row "QueryEditor" link → read-only Query popup
                      (owner report 2026-10-22). */}
                  <button type="button" className="mlog-qe-link" title="View the import query for this sheet"
                    onClick={() => setQueryView(r.query)}>
                    QueryEditor
                  </button>
                </td>
                <td /><td>{currentUserName}</td><td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add MDI Name popup (legacy Initialization Templates "New", image 397) — the
          FULL editor (MDI Version · MDI Name* · Attachment Browse/Load/Reset · Templates
          grid) reused via <MdiTemplatesDetail embedded>. On save, select the new MDI. */}
      {addOpen && (
        <AppDialog
          open
          title="Add MDI Name"
          icon="ti-database-plus"
          width={960}
          onClose={() => setAddOpen(false)}
        >
          <MdiTemplatesDetail
            embedded
            id={null}
            onClose={() => setAddOpen(false)}
            onSaved={(newId) => {
              setAddOpen(false);
              if (newId) setMdiOid(newId);   // select the freshly-created MDI
              void loadOptions();            // refresh the dropdown to include it
            }}
          />
        </AppDialog>
      )}
      {queryView !== null && (
        <AppDialog
          open
          title="Initialization Template"
          icon="ti-table"
          width={760}
          onClose={() => setQueryView(null)}
          footer={<button className="ca-btn ca-btn-solid" onClick={() => setQueryView(null)}>Close</button>}
        >
          <div className="mlog-qe">
            <label className="mlog-qe-lbl">Query:</label>
            <textarea className="mlog-qe-area" value={queryView} readOnly spellCheck={false} />
          </div>
        </AppDialog>
      )}
      <style>{styles}</style>
    </AppDialog>
  );
}

const styles = `
  .mlog-form { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
  .mlog-row { display: flex; align-items: center; gap: 10px; }
  .mlog-lbl { width: 92px; flex: 0 0 92px; font-size: 13px; color: var(--ai-ink, #1f2430); }
  .mlog-in { flex: 1 1 auto; max-width: 300px; height: 28px; padding: 0 8px; border: 1px solid var(--ai-border, #d4d8e0); border-radius: 0; font-size: 13px; }
  /* One focus look for every input here: square, the orange edge, no glow (the combo already takes the orange edge). */
  .mlog-in:focus, .mlog-qe-area:focus { border-color: var(--brand-accent, var(--ai-orange)); outline: none; box-shadow: none; }
  .mlog-mdi .ai-lkbox { border-radius: 0; }
  /* MDI Name = shared AiCombo (value + welded "＋ New" + caret as one control),
     matching the Sample Registration lookups (owner request 2026-10-16). */
  .mlog-mdi { display: flex; align-items: center; flex: 1 1 auto; max-width: 300px; }
  .mlog-mdi .ai-lkbox { flex: 1 1 auto; width: 100%; }
  /* De-orange the welded "＋" + caret to the green-on-white XAF look — same as the
     Category combo (.mt-cat-combo) and the shared .mlv-welded lists (owner request
     2026-10-16, image 723 — remove the orange "+" background). Scoped to .mlog-mdi,
     so the shared AiCombo's orange group is unchanged everywhere else. */
  .mlog-mdi .aic-new { background: transparent; border-left: 1px solid var(--ai-line); color: var(--ai-icon-new, #1f9159); }
  .mlog-mdi .aic-new:hover:not(:disabled) { background: var(--ai-row-hover, #f1f3f7); color: var(--ai-icon-new, #1f9159); }
  .mlog-mdi .aic-caret { background: var(--ai-card, #fff); border-left: 1px solid var(--ai-line); color: var(--ai-muted); }
  .mlog-mdi .aic-caret:hover:not(:disabled) { background: var(--ai-row-hover, #f1f3f7); color: var(--ai-muted); }
  .mlog-mdi .aic-caret-tri { border-top-color: var(--ai-muted); }
  .mlog-att { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .mlog-file { font-size: 12px; }
  .mlog-sec { font-size: 14px; font-weight: 600; color: var(--ai-ink, #1f2430); margin: 2px 0 8px; }
  .mlog-gridwrap { border: 1px solid var(--ai-border, #e3e6ec); border-radius: 6px; overflow: auto; max-height: 340px; background: var(--ai-card, #fff); }
  .mlog-grid { width: 100%; border-collapse: collapse; font-size: 13px; }
  .mlog-grid th { text-align: left; padding: 8px; background: #fafbfc; color: var(--ai-ink, #1f2430); font-weight: 600; border-bottom: 1px solid var(--ai-border, #e3e6ec); white-space: nowrap; }
  .mlog-grid td { padding: 6px 8px; border-bottom: 1px solid var(--ai-border, #eef0f4); color: var(--ai-ink, #1f2430); }
  .mlog-qe-link { border: 0; background: transparent; color: var(--ai-orange, #e8670a); cursor: pointer; padding: 2px 4px; font-size: 13px; }
  .mlog-qe-link:hover { text-decoration: underline; }
  .mlog-qe { display: flex; gap: 10px; align-items: flex-start; }
  .mlog-qe-lbl { flex: 0 0 auto; color: var(--ai-ink, #1f2430); font-size: 13px; padding-top: 6px; }
  .mlog-qe-area { flex: 1 1 auto; min-height: 280px; resize: vertical; padding: 8px; border: 1px solid var(--ai-border, #d4d8e0); border-radius: 0; font-family: "JetBrains Mono", Consolas, monospace; font-size: 12.75px; box-sizing: border-box; background: var(--ai-readonly-bg, #f5f6f8); }
  .mlog-empty { text-align: center; color: var(--ai-muted, #6b7280); padding: 20px; }
`;
